import { useEffect } from 'react';

type IdleCallbackHandle = number;

type IdleCallbackDeadline = {
    readonly didTimeout: boolean;
    timeRemaining: () => number;
};

type IdleCallbackOptions = {
    timeout?: number;
};

type RequestIdleCallback = (
    callback: (deadline: IdleCallbackDeadline) => void,
    options?: IdleCallbackOptions,
) => IdleCallbackHandle;

type CancelIdleCallback = (handle: IdleCallbackHandle) => void;

export function useIdleEffect(effect: () => void | (() => void), deps: React.DependencyList = []) {
    useEffect(() => {
        let cleanup: void | (() => void);
        let idleCallbackId: number;

        const requestIdleCallbackFn = (window as any).requestIdleCallback as RequestIdleCallback | undefined;
        const cancelIdleCallbackFn = (window as any).cancelIdleCallback as CancelIdleCallback | undefined;

        if (typeof requestIdleCallbackFn === 'function') {
            idleCallbackId = requestIdleCallbackFn(() => {
                cleanup = effect();
            });
        } else {
            // Safari or unsupported: fallback to timeout
            idleCallbackId = window.setTimeout(() => {
                cleanup = effect();
            }, 200);
        }

        return () => {
            if (typeof cancelIdleCallbackFn === 'function') {
                cancelIdleCallbackFn(idleCallbackId);
            } else {
                clearTimeout(idleCallbackId);
            }

            if (typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, deps);
}
